import React, { useState, createRef } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash.get';
import {
  Overlay,
  Box,
  Container,
  Flex,
  FullLogo,
  IconButton,
  Typography,
  TextInput,
  Button,
  color
} from '@kistech/kis-ui';
import NoImagePreview from 'assets/no_image_preview.png';
import { useActions } from 'hooks/useActions';
import { actions as mediaActions, selectors as mediaSelectors } from 'store/media';
import { validateFileName, isValidImage, isValidResolution } from 'utils/validation';
import { ImageUploadPreview } from './ImageUploadPreview';
import { ImageUploadOverlayTypes } from '../../../types';
const INVALID_FILE_NAME = 'Invalid image name format';

export const ImageUploadOverlay: React.FC<ImageUploadOverlayTypes> = (props: ImageUploadOverlayTypes) => {
  // replace '/' that could be contained in genre/event/venue names
  const imageName = props.imageName ? props.imageName.replace(new RegExp('/', 'g'), ' ') : '';
  const [selectedFile, setSelectedFile] = useState<null | File>(null);
  const [fileUrl, setFileUrl] = useState<string>(NoImagePreview);
  const [filename, setFilename] = useState<string>(imageName || '');
  const [invalidFile, setInvalidFile] = useState<boolean>(false);
  const [createMedia, resetAction, setUploadImageError] = useActions([
    mediaActions.createMedia,
    mediaActions.resetAction,
    mediaActions.setUploadImageError
  ]);
  const { pending, successful, response, error } = useSelector(mediaSelectors.selectAction);

  const fileUploadRef = createRef<HTMLInputElement>();

  const isSaveDisabled = !selectedFile || !filename || invalidFile;

  React.useEffect(() => {
    resetAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // FILE NAME VALIDATION
  const fileNameValidationFn = async (value: string | number | undefined) => {
    if (value && value.toString().trim().length) {
      // VALIDATION SCHEMA
      const schema = validateFileName(INVALID_FILE_NAME);

      // VALIDATE FN
      const result = await schema.validate(value.toString().trim()).catch((err) => err);
      // IF THERE WAS ERROR MESSAGE - SET ERROR STATE AND FILE INVALID
      setUploadImageError(result && result.message);
      setInvalidFile(result && result.message);
      if ((!result || !result.message) && selectedFile) {
        const err = isValidImage(selectedFile);
        if (err) {
          setUploadImageError(err);
        }
      }
    }
  };

  const handleClickUploadImage = () => {
    const file = selectedFile as File;
    const name = `${filename}.${file.name.split('.').pop()}`;
    const fileName = file.name;
    createMedia({
      file,
      name,
      fileName,
      type: props.imageType,
      isEventImage: ['event', 'venue', 'genre', 'promo-code-image'].includes(props.imageType),
      clientIntegrationId: props.clientIntegrationId,
      configurationId: props.configurationId
    });
  };

  const triggerFileSelector = () => {
    if (fileUploadRef.current) fileUploadRef.current.click();
  };

  const getDimensions = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = get(e, ['target', 'files', 0]);
    if (file) {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        const width = img.width;
        const height = img.height;
        handleFileSelection(file, height, width);
      };
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFileSelection = (file: any, imgHeight: number, imgWidth: number) => {
    if (file) {
      let err = isValidImage(file, props.imageType);
      if (!err) {
        err = isValidResolution(props.imageType, imgHeight, imgWidth);
      }

      if (err) {
        setUploadImageError(err);
        setSelectedFile(null);
        setFileUrl(NoImagePreview);
      } else {
        setUploadImageError('');
        setSelectedFile(file);
        setFileUrl(URL.createObjectURL(file));
        if (['upper-image', 'attract-image'].includes(props.imageType) && filename) {
          fileNameValidationFn(filename);
        } else if (
          ['inactive-image', 'splash-image', 'logo-image', 'logo-image-large', 'background-image'].includes(
            props.imageType
          )
        ) {
          setFilename(file.name);
        }
      }
    }
  };

  const handleFilenameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFilename = event.target.value.trim();
    fileNameValidationFn(newFilename);
    setFilename(newFilename);
  };

  const getRecommendedResolution = (): string => {
    let resolution = '';
    if (
      props.imageType === 'event' ||
      props.imageType === 'genre' ||
      props.imageType === 'venue' ||
      props.imageType === 'promo-code-image'
    ) {
      resolution = '800px by 600px (4:3 aspect ratio)';
    } else if (props.imageType === 'inactive-image') {
      resolution = 'Landscape: \n\tSplit-Screen: 860px by 880px \n\tFull-Screen: 1720px by 880px';
      resolution += '\nPortrait: \n\tSplit-Screen: 880px by 880px \n\tFull-Screen: 880px by 1720px';
    } else if (props.imageType === 'splash-image') {
      resolution = 'Landscape: \n\tSplit-Screen: 860px by 880px \n\tFull-Screen: 1720px by 780px';
      resolution += '\nPortrait: \n\tSplit-Screen: 880px by 915px \n\tFull-Screen: 880px by 1550px';
    } else if (props.imageType === 'logo-image') {
      resolution = '160px by 50px';
    } else if (props.imageType === 'logo-image-large') {
      resolution = '320px by 100px';
    } else if (props.imageType === 'background-image') {
      resolution = 'Landscape: 1920px by 1080px';
      resolution += '\nPortrait: 1080px by 1920px';
    } else if (props.imageType === 'attract-image') {
      resolution = 'Landscape: 860px by 880px';
      resolution += '\nPortrait: 880px by 880px';
    } else if (props.imageType === 'upper-image') {
      resolution = 'Landscape: 1920px by 1080px';
      resolution += '\nPortrait: 1080px by 1920px';
    } else {
      resolution = props.halfPreview ? '860px by 880px' : '1920px by 1080px.';
    }

    return resolution;
  };

  if (successful && response) {
    props.onSuccess(response);
  }

  return (
    <Overlay>
      <Box bg="x-light-gray" height="100vh" width="100%">
        <Container fullHeight maxWidth={1440}>
          <Box bg="white" style={{ borderRadius: '32px 32px 0 0' }} overflow="auto">
            {/* OVERLAY HEADER */}
            <Flex justify="between" padding="6 11 6" margin="0 0 5">
              <FullLogo />
              <IconButton
                data-testid="add-image-x"
                name="exit_circle"
                onClick={props.onCancel}
                appearance="transparent"
                size={40}
              />
            </Flex>

            <Flex justify="center" flexOverride="1 1 0%" padding="0 0 11">
              <Flex width={624}>
                <Typography size={40} weight={600}>
                  Add Image
                </Typography>
                <Typography size={20} style={{ width: '100%' }}>
                  {['upper-image', 'attract-image'].includes(props.imageType)
                    ? 'Select an image and provide a custom name'
                    : ''}
                </Typography>
                <Box fullHeight margin="6 0">
                  <input
                    type="file"
                    id="file"
                    ref={fileUploadRef}
                    style={{ display: 'none' }}
                    onChange={getDimensions}
                    accept="image/png, image/jpeg"
                    data-testid="file"
                  />
                  <Typography size={24} weight={600}>
                    Select Image
                  </Typography>
                  <Box margin="2 0">
                    <Button size="small" width={248} appearance="white" onClick={triggerFileSelector}>
                      Upload
                    </Button>
                  </Box>
                  <Box margin="3 4 5" style={{ whiteSpace: 'pre-wrap' }}>
                    <Typography size={12} weight={600}>
                      Recommended Resolution
                    </Typography>
                    <Typography size={12}>{getRecommendedResolution()}</Typography>
                  </Box>
                  <Box margin="0 4">
                    <Typography size={12} weight={600} color={color.RED}>
                      Requirements
                    </Typography>
                    <Typography size={12}>
                      {['logo-image', 'logo-image-large'].includes(props.imageType)
                        ? 'File type(s) accepted: png'
                        : 'File type(s) accepted: jpg, png'}
                      <br />
                      File size should be less than 10MB
                    </Typography>
                  </Box>
                  {['upper-image', 'attract-image'].includes(props.imageType) && (
                    <>
                      <Box margin="6 0 2">
                        <Typography size={24} weight={600}>
                          Create a Name
                        </Typography>
                      </Box>
                      <Box width={220}>
                        <TextInput
                          placeholder="Name of Image"
                          fullWidth
                          size="medium"
                          onChange={handleFilenameChange}
                          validate={fileNameValidationFn}
                          maxLength={19}
                        />
                      </Box>
                    </>
                  )}
                  <Box padding="6 4 6 0">{error && <Typography color={color.RED}>{error}</Typography>}</Box>
                  {/* <Box margin="6 0" /> */}
                  <Button
                    size="x-small"
                    width={248}
                    isDisabled={isSaveDisabled}
                    appearance="primary"
                    isLoading={pending}
                    onClick={handleClickUploadImage}
                  >
                    {['upper-image', 'attract-image'].includes(props.imageType) ? 'Add to slideshow' : 'Save Image'}
                  </Button>
                </Box>
                <Box fullHeight margin="6 0">
                  <Box margin="0 6">
                    <Typography size={24} weight={600}>
                      Image Preview
                    </Typography>
                    <Box margin="2 0">
                      {selectedFile ? (
                        <>
                          <ImageUploadPreview
                            width={200}
                            fileUrl={fileUrl}
                            halfPreview={props.halfPreview}
                            imageType={props.imageType}
                          />
                          <Box width={200} margin="4 0">
                            <Typography size={12}>
                              This preview demonstrates how your image will appear on the screen.
                            </Typography>
                          </Box>
                        </>
                      ) : (
                        <img alt="preview" width={150} height={150} src={fileUrl} data-testid="no-image" />
                      )}
                    </Box>
                  </Box>
                </Box>
              </Flex>
            </Flex>
          </Box>
        </Container>
      </Box>
    </Overlay>
  );
};
